import { Box, Card, Center, Group, Stack, Title, useMatches } from '@mantine/core'
import { IconBookmark, IconBookmarkFilled, IconEyeFilled, IconEyeOff } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { animate, cubicBezier, motion, MotionValue, useMotionValue, useTransform } from 'motion/react'
import { type ReactNode, useCallback, useEffect } from 'react'
import type { Movie } from '../../api_types.ts'
import { trpc } from '../queryClient.ts'
import { useErrorNotifier } from '../tools/errors.tsx'
import { MovieActions } from './movie_actions.tsx'

const SWIPE_DISTANCE_THRESHOLD = 150

export function MovieCard({ movie, children }: { movie: Movie; children?: ReactNode }) {
  const x = useMotionValue(0)
  const drag = useMatches<'x' | false>({ base: 'x', md: false })
  const resetCardAnimations = useCallback(() => void animate(x, 0, { duration: 0.2 }), [x])

  const queryClient = useQueryClient()

  function onMovieUpdate() {
    void queryClient.invalidateQueries({ queryKey: trpc.moviesInCinemas.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.upcomingMovies.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.movie.all.queryKey() })
    void queryClient.invalidateQueries({ queryKey: trpc.movie.info.queryKey(movie.id) })
  }

  const { mutate: setHidden, isPending: hidePending, error: hideError } = useMutation(trpc.user.setMovieHidden.mutationOptions({
    onSuccess: onMovieUpdate,
    onError: resetCardAnimations,
  }))
  useErrorNotifier('Error hiding movie', hideError)
  const { mutate: setSaved, isPending: savePending, error: saveError } = useMutation(trpc.user.setMovieSaved.mutationOptions({
    onSuccess: onMovieUpdate,
    onError: resetCardAnimations,
  }))
  useErrorNotifier('Error hiding movie', saveError)

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ position: 'relative', overflowX: 'hidden' }}>
      <CardBackground x={x} isSaved={movie.saved} isHidden={movie.hidden}/>
      <motion.div
        drag={drag}
        dragElastic={0}
        style={{ x }}
        onDragEnd={async (_e, info) => {
          if (info.offset.x > SWIPE_DISTANCE_THRESHOLD) {
            setHidden({ movieId: movie.id, hidden: !movie.hidden })
            await animate(x, 800, { duration: 0.2 })
            await animate(x, 0, { duration: 0 })
          } else if (info.offset.x < -SWIPE_DISTANCE_THRESHOLD) {
            setSaved({ movieId: movie.id, saved: !movie.saved })
            await animate(x, -800, { duration: 0.2 })
            await animate(x, 0, { duration: 0 })
          } else {
            await animate(x, 0, { duration: 0.1 })
          }
        }}
      >
        <Link to="/movie/$movieId" params={{ movieId: movie.id.toString() }} style={{ textDecoration: 'none' }}>
          <Card p={0} style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
            <MovieAttributeIcons movie={movie}/>
            <Box w={{ base: '80px', sm: '120px' }} h={{ base: '120px', sm: '180' }} style={{ flexShrink: 0 }}>
              <img src={movie.posterPath} alt={`${movie.title} poster`} style={{ width: '100%', height: '100%' }}/>
            </Box>

            <Stack p="md" w="100%" align="flex-start" justify="space-between">
              <Box>
                <Title order={3} lineClamp={1} mb="sm">{movie.title}</Title>
                {children}
              </Box>
              <Box visibleFrom="sm">
                <MovieActions
                  movie={movie}
                  isSaving={savePending}
                  isHiding={hidePending}
                  onSave={() => setSaved({ movieId: movie.id, saved: !movie.saved })}
                  onHide={() => setHidden({ movieId: movie.id, hidden: !movie.hidden })}
                />
              </Box>
            </Stack>
          </Card>
        </Link>
      </motion.div>
    </motion.div>
  )
}

function CardBackground({ x, isSaved, isHidden }: { x: MotionValue<number>, isSaved: boolean, isHidden: boolean }) {
  const { hideOpacity, saveOpacity } = useTransform(x, [-200, 0, 200], {
    hideOpacity: [0, 0, 1],
    saveOpacity: [1, 0, 0],
  })
  const scale = useTransform(
    x,
    [-SWIPE_DISTANCE_THRESHOLD, 0, SWIPE_DISTANCE_THRESHOLD],
    [1.2, 1, 1.2],
    { ease: cubicBezier(0, 1, 1, 0) },
  )

  return (
    <Group pos="absolute" justify="space-between" inset="0" bg="primary.9" style={{ borderRadius: '0.25rem' }}>
      <motion.div style={{ margin: '0 40px', opacity: hideOpacity, scale }}>
        <Center>
          {isHidden ? <IconEyeFilled size="50"/> : <IconEyeOff size="60"/>}
        </Center>
      </motion.div>
      <motion.div style={{ margin: '0 40px', opacity: saveOpacity, scale }}>
        <Center>
          {isSaved ? <IconBookmark size="50"/> : <IconBookmarkFilled size="60"/>}
        </Center>
      </motion.div>
    </Group>
  )
}

function MovieAttributeIcons({ movie }: { movie: Movie }) {
  const savedScale = useMotionValue(1)
  const hiddenScale = useMotionValue(1)

  useEffect(() => void animate(savedScale, [1, 1, 1.5, 1], { duration: 0.5 }), [movie.saved])
  useEffect(() => void animate(hiddenScale, [1, 1, 1.5, 1], { duration: 0.5 }), [movie.hidden])

  return (
    <Group hiddenFrom="sm" c="dark.2" gap="xs" pos="absolute" top="8px" right="8px">
      {movie.saved && (
        <motion.div style={{ scale: savedScale }}>
          <IconBookmarkFilled/>
        </motion.div>
      )}
      {movie.hidden && (
        <motion.div style={{ scale: hiddenScale }}>
          <IconEyeOff/>
        </motion.div>
      )}
    </Group>
  )
}
